import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const IDLE_TIMEOUT_MS = 5 * 60 * 60 * 1000; // 5 hours in milliseconds
const IDLE_CHECK_INTERVAL_MS = 60 * 1000;    // check every 60 seconds
const LAST_ACTIVITY_KEY = 'assurex_last_activity';

interface User {
  email: string;
  name: string;
  role: string;
  is_approved: boolean;
  is_active: boolean;
  picture_url?: string;
}

interface AuthTokens {
  access_token: string;
  refresh_token: string;
  token_type: string;
}

interface AuthContextType {
  user: User | null;
  tokens: AuthTokens | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, fullName: string) => Promise<void>;
  loginWithToken: (accessToken: string, refreshToken: string) => void;
  logout: () => void;
  refreshAccessToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [tokens, setTokens] = useState<AuthTokens | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const idleCheckRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Load tokens from localStorage on mount
  useEffect(() => {
    const loadTokens = () => {
      try {
        const storedTokens = localStorage.getItem('auth_tokens');
        const storedUser = localStorage.getItem('auth_user');
        
        if (storedTokens && storedUser) {
          setTokens(JSON.parse(storedTokens));
          setUser(JSON.parse(storedUser));
        }
      } catch (error) {
        console.error('Error loading auth data:', error);
        localStorage.removeItem('auth_tokens');
        localStorage.removeItem('auth_user');
      } finally {
        setIsLoading(false);
      }
    };

    loadTokens();
  }, []);

  // Save tokens to localStorage whenever they change
  useEffect(() => {
    if (tokens) {
      localStorage.setItem('auth_tokens', JSON.stringify(tokens));
    } else {
      localStorage.removeItem('auth_tokens');
    }
  }, [tokens]);

  // Save user to localStorage whenever it changes
  useEffect(() => {
    if (user) {
      localStorage.setItem('auth_user', JSON.stringify(user));
    } else {
      localStorage.removeItem('auth_user');
    }
  }, [user]);

  // ── Idle timeout: force logout after 5 hours of inactivity ──────────────
  useEffect(() => {
    if (!user) return; // only track when logged in

    // Record activity on any user interaction
    const updateActivity = () => {
      localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
    };

    // Set initial activity timestamp on login
    updateActivity();

    // Listen for any user interaction events
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    events.forEach(e => window.addEventListener(e, updateActivity, { passive: true }));

    // Check for inactivity every minute
    idleCheckRef.current = setInterval(() => {
      const lastActivity = parseInt(localStorage.getItem(LAST_ACTIVITY_KEY) || '0', 10);
      if (lastActivity && Date.now() - lastActivity > IDLE_TIMEOUT_MS) {
        // 5 hours of inactivity — force logout
        console.info('[AssureX] Session expired due to inactivity. Logging out.');
        clearInterval(idleCheckRef.current!);
        // Call logout inline to avoid stale closure
        setUser(null);
        setTokens(null);
        localStorage.removeItem('auth_tokens');
        localStorage.removeItem('auth_user');
        localStorage.removeItem('user_email');
        localStorage.removeItem('user_name');
        localStorage.removeItem('user_picture');
        localStorage.removeItem(LAST_ACTIVITY_KEY);
        window.location.href = '/login?reason=idle';
      }
    }, IDLE_CHECK_INTERVAL_MS);

    return () => {
      events.forEach(e => window.removeEventListener(e, updateActivity));
      if (idleCheckRef.current) clearInterval(idleCheckRef.current);
    };
  }, [user]); // re-run when user logs in or out

  const login = async (email: string, password: string) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/v1/auth/login`, {
        email,
        password,
      });

      const authData = response.data;
      setTokens({
        access_token: authData.access_token,
        refresh_token: authData.refresh_token,
        token_type: authData.token_type,
      });
      setUser(authData.user);
    } catch (error: any) {
      if (error.response?.data?.detail) {
        throw new Error(error.response.data.detail);
      }
      throw new Error('Login failed. Please check your credentials.');
    }
  };

  const loginWithToken = (accessToken: string, refreshToken: string) => {
    const userEmail = localStorage.getItem('user_email') || '';
    const userName = localStorage.getItem('user_name') || '';
    const userRole = localStorage.getItem('user_role') || 'Viewer';
    
    const newTokens = {
      access_token: accessToken,
      refresh_token: refreshToken,
      token_type: 'bearer',
    };
    
    const newUser = {
      email: userEmail,
      name: userName,
      role: userRole,
      is_approved: true,
      is_active: true,
      picture_url: localStorage.getItem('user_picture') || undefined,
    };
    
    setTokens(newTokens);
    setUser(newUser);
  };

  const register = async (email: string, password: string, fullName: string) => {
    try {
      await axios.post(`${API_BASE_URL}/api/v1/auth/register`, {
        email,
        password,
        name: fullName,
      });
    } catch (error: any) {
      if (error.response?.data?.detail) {
        throw new Error(error.response.data.detail);
      }
      throw new Error('Registration failed. Please try again.');
    }
  };

  const logout = () => {
    setUser(null);
    setTokens(null);
    localStorage.removeItem('auth_tokens');
    localStorage.removeItem('auth_user');
    // Also clear old header-based auth data if it exists
    localStorage.removeItem('user_email');
    localStorage.removeItem('user_name');
    localStorage.removeItem('user_picture');
    localStorage.removeItem(LAST_ACTIVITY_KEY);
    if (idleCheckRef.current) clearInterval(idleCheckRef.current);
  };

  const refreshAccessToken = async (): Promise<string | null> => {
    try {
      if (!tokens?.refresh_token) {
        return null;
      }

      const response = await axios.post(`${API_BASE_URL}/api/v1/auth/refresh`, {
        refresh_token: tokens.refresh_token,
      });

      const newTokens = {
        access_token: response.data.access_token,
        refresh_token: response.data.refresh_token,
        token_type: response.data.token_type,
      };

      setTokens(newTokens);
      return newTokens.access_token;
    } catch (error) {
      console.error('Token refresh failed:', error);
      logout();
      return null;
    }
  };

  const value: AuthContextType = {
    user,
    tokens,
    isAuthenticated: !!user && !!tokens,
    isLoading,
    login,
    register,
    loginWithToken,
    logout,
    refreshAccessToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
